<template>
  <div ref="blockRef" class="grid grid-cols-1 gap-8 mx-auto px-4 max-w-6xl lg:grid-cols-2 lg:gap-24">
    <div>
      <h2
        v-if="block.title"
        v-html="formatTitle(block.title)"
        :class="`text-[clamp(1.5rem,-4.125rem+28.125vw,3.75rem)] leading-none whitespace-nowrap max-w-md ${block.title_color.value}`"
        data-gsap-target="title"
      />
    </div>

    <div v-if="block.list" class="p-6 bg-white rounded-4xl md:p-12">
      <Disclosure
        v-for="(item, index) in block.list"
        :key="item.id"
        v-slot="{ open, close }"
        as="div"
        data-gsap-target="listItem"
      >
        <div :class="`border-b border-purple-dark ${index !== 0 ? 'py-6' : 'pb-6'}`">
          <DisclosureButton class="relative pl-8 font-serif text-left text-lg text-purple-dark leading-tight md:text-xl">
            <span
              :class="`
                absolute top-2 left-0 inline-block h-3.5 w-3.5
                before:absolute before:top-1/2 before:left-1/2 before:-translate-y-1/2 before:-translate-x-1/2 before:h-0.5 before:w-3.5 before:bg-purple-dark before:transition-opacity
                after:absolute after:top-1/2 after:left-1/2 after:-translate-y-1/2 after:-translate-x-1/2 after:h-3.5 after:w-0.5 after:bg-purple-dark after:transition-opacity
                ${open ? 'after:opacity-0' : ''}
              `"
            />

            {{ item.list_item_title }}
          </DisclosureButton>

          <DisclosurePanel
            static
            :class="`${open ? 'opacity-100 max-h-none' : 'opacity-0 max-h-0'} overflow-hidden transition-all duration-300`"
          >
            <div
              v-if="item.list_item_text"
              v-html="item.list_item_text"
              class="prose pt-4 pl-8 text-purple-dark"
            />
          </DisclosurePanel>
        </div>

        <button
          :ref="(el) => (disclosures[index] = el)"
          :data-id="item.id"
          v-show="false"
          @click="closeDisclosure(close)"
        />
        <DisclosureStateEmitter :show="open" @show="closeOtherDisclosures(item.id)" />
      </Disclosure>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  provideUseId,
} from '@headlessui/vue';
import type { TitleList } from '~/statamic/types/block';

defineProps<{
  block: TitleList;
}>();

provideUseId(() => useId());

let ctx: gsap.Context | null;
const blockRef = ref();

const disclosures = ref([]);

const closeOtherDisclosures = (id) => {
  const items = disclosures.value.filter((elm) => {
    return elm.getAttribute('data-id') !== id.toString();
  });

  items.forEach((elm) => elm.click());
};

const closeDisclosure = (close) => {
  close();
};

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const titleLeft = self.selector('[data-gsap-target="title"]')[0];
    const titleRight = self.selector('[data-gsap-target="title"] > .text-right')[0];
    const listItems = self.selector('[data-gsap-target="listItem"]');

    const timeline = useGsap.timeline({
      scrollTrigger: {
        start: 'top bottom',
        trigger: blockRef.value,
      },
    });

    if (titleLeft) {
      timeline.from(titleLeft, {
        x: -100,
        autoAlpha: 0,
      });
    }

    if (titleRight) {
      timeline.from(titleRight, {
        x: 100,
        autoAlpha: 0,
      }, '-=0.25');
    }

    if (listItems?.length) {
      timeline.from(listItems, {
        x: 50,
        y: 50,
        stagger: 0.25,
        autoAlpha: 0,
      });
    }
  }, blockRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
